import type {
    DashboardCacheVersions,
    DashboardMapConfig,
    DashboardPermissions,
    DashboardScope,
} from "@/core/interfaces/Dashboard";
import type DashboardModule from "@/store/modules/DashboardModule";
import { computed } from "vue";
import { useStore } from "vuex";

function dashboardState(store: ReturnType<typeof useStore>): DashboardModule {
  return (store.state as { DashboardModule: DashboardModule }).DashboardModule;
}

export function useDashboardStore() {
  const store = useStore();
  const state = computed(() => dashboardState(store));

  const scope = computed(() => state.value.scope);
  const versions = computed(() => state.value.versions);
  const permissions = computed(() => state.value.permissions);
  const mapConfig = computed(() => state.value.mapConfig);
  const selectedMarkerId = computed(() => state.value.selectedMarkerId);
  const metaLoaded = computed(() => state.value.metaLoaded);
  const capDonVi = computed(() => state.value.capDonVi);
  const movementGroupBy = computed(() => state.value.movementGroupBy);
  const movementMetric = computed(() => state.value.movementMetric);
  const fullscreenWidget = computed(() => state.value.fullscreen.widget);

  function setScope(next: DashboardScope): void {
    store.commit("DashboardModule/setScope", next);
  }

  function patchScope(partial: Partial<DashboardScope>): void {
    store.commit("DashboardModule/patchScope", partial);
  }

  function setDateRange(fromDate: string, toDate: string): void {
    store.commit("DashboardModule/setDateRange", { fromDate, toDate });
  }

  function setSelectedMarkerId(id: number | null): void {
    store.commit("DashboardModule/setSelectedMarkerId", id);
  }

  function setVersions(next: DashboardCacheVersions): void {
    store.commit("DashboardModule/setVersions", next);
  }

  function setPermissions(next: DashboardPermissions): void {
    store.commit("DashboardModule/setPermissions", next);
  }

  function setMapConfig(next: DashboardMapConfig): void {
    store.commit("DashboardModule/setMapConfig", next);
  }

  function setCapDonVi(cap: number): void {
    store.commit("DashboardModule/setCapDonVi", cap);
  }

  function setMetaLoaded(loaded: boolean): void {
    store.commit("DashboardModule/setMetaLoaded", loaded);
  }

  function setFullscreenWidget(widget: string | null): void {
    store.commit("DashboardModule/setFullscreenWidget", widget);
  }

  function setMovementOptions(payload: {
    groupBy?: "day" | "week" | "month";
    metric?: "amount" | "quantity";
  }): void {
    store.commit("DashboardModule/setMovementOptions", payload);
  }

  function resetDashboardUi(): void {
    store.commit("DashboardModule/resetDashboardUi");
  }

  function resetDashboardState(): void {
    store.commit("DashboardModule/resetDashboardState");
  }

  return {
    scope,
    versions,
    permissions,
    mapConfig,
    selectedMarkerId,
    metaLoaded,
    capDonVi,
    movementGroupBy,
    movementMetric,
    fullscreenWidget,
    setScope,
    patchScope,
    setDateRange,
    setSelectedMarkerId,
    setVersions,
    setPermissions,
    setMapConfig,
    setCapDonVi,
    setMetaLoaded,
    setFullscreenWidget,
    setMovementOptions,
    resetDashboardUi,
    resetDashboardState,
  };
}
